import { ImageAnalysisSample } from '@aloft/toolkit-shared';
import { Block, Button, Checkbox, Flex, SimpleInput, SimpleSelect, Typography } from '@aloft/toolkit-sui';
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { AnalysisMode, DEFAULT_MODEL, DEFAULT_PROMPTS, GEMINI_MODELS } from './imageAnalysisFormConfig.ts';
import { RatingMultiSelect } from './RatingMultiSelect.tsx';

export type BulkAnalysisFormSubmit = {
  count: number;
  analysisMode: AnalysisMode;
  model: string;
  promptOverride?: string;
  sample: ImageAnalysisSample;
  compress: boolean;
  qualityFilter?: number[];
  conditionFilter?: number[];
  stateFilter?: string;
  zipFilter?: string;
};

type Props = {
  onSubmit: (values: BulkAnalysisFormSubmit) => void;
  isSubmitting: boolean;
  analysisMode: AnalysisMode;
};


export const BulkAnalysisForm = ({ onSubmit, isSubmitting, analysisMode }: Props) => {
  const [count, setCount] = useState('20');
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [sampleType, setSampleType] = useState<'all' | 'first' | 'random'>('random');
  const [sampleCount, setSampleCount] = useState('15');
  const [compress, setCompress] = useState(true);
  const [qualityValues, setQualityValues] = useState<string[]>([]);
  const [conditionValues, setConditionValues] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState('');
  const [zipFilter, setZipFilter] = useState('');
  const [promptOverride, setPromptOverride] = useState(DEFAULT_PROMPTS[analysisMode]);
  const [overrideEnabled, setOverrideEnabled] = useState(false);

  useEffect(() => {
    setPromptOverride(DEFAULT_PROMPTS[analysisMode]);
    setOverrideEnabled(false);
  }, [analysisMode]);

  const handleSubmit = () => {
    const parsedCount = Math.max(1, Math.min(200, parseInt(count, 10) || 20));
    const parsedSampleCount = Math.max(1, parseInt(sampleCount, 10) || 15);

    let sample: ImageAnalysisSample;
    if (sampleType === 'random') {
      sample = { type: 'random', count: parsedSampleCount };
    } else if (sampleType === 'first') {
      sample = { type: 'first', count: parsedSampleCount };
    } else {
      sample = { type: 'all' };
    }

    onSubmit({
      count: parsedCount,
      analysisMode,
      model,
      promptOverride: overrideEnabled ? promptOverride : undefined,
      sample,
      compress,
      qualityFilter: qualityValues.length > 0 ? qualityValues.map(Number) : undefined,
      conditionFilter: conditionValues.length > 0 ? conditionValues.map(Number) : undefined,
      stateFilter: stateFilter.trim() || undefined,
      zipFilter: zipFilter.trim() || undefined,
    });
  };

  return (
    <Flex gap="24px" flexDirection="column">
      <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
        <SimpleInput
          label="Number of comps"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          size="sm"
          width="140px"
          placeholder="20"
        />

        <SimpleSelect
          label="Model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          width="300px"
          size="sm"
        >
          {GEMINI_MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </SimpleSelect>
      </Flex>

      <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
        <SimpleSelect
          label="Images per comp"
          value={sampleType}
          onChange={(e) => setSampleType(e.target.value as 'all' | 'first' | 'random')}
          width="160px"
          size="sm"
        >
          <option value="all">All images</option>
          <option value="first">First N</option>
          <option value="random">Random sample</option>
        </SimpleSelect>

        {(sampleType === 'random' || sampleType === 'first') && (
          <SimpleInput
            label="Count"
            value={sampleCount}
            onChange={(e) => setSampleCount(e.target.value)}
            size="sm"
            width="80px"
            placeholder="15"
          />
        )}

        <Checkbox isChecked={compress} onChange={(e) => setCompress(e.target.checked)}>
          Compress images
        </Checkbox>
      </Flex>

      <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
        <RatingMultiSelect
          label="Quality"
          prefix="Q"
          value={qualityValues}
          onChange={setQualityValues}
        />

        <RatingMultiSelect
          label="Condition"
          prefix="C"
          value={conditionValues}
          onChange={setConditionValues}
        />

        <SimpleInput
          label="State"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
          size="sm"
          width="80px"
          placeholder="FL"
        />

        <SimpleInput
          label="Zip code"
          value={zipFilter}
          onChange={(e) => setZipFilter(e.target.value)}
          size="sm"
          width="110px"
          placeholder="90210"
        />
      </Flex>

      {analysisMode === 'craftsmanQuality' && (
        <Block padding="10px 16px" borderRadius="4px" backgroundColor="alertWarningLight">
          <Typography variant="body2">
            Craftsman mode does not produce a condition rating — only quality accuracy will be reported.
          </Typography>
        </Block>
      )}

      <Flex flexDirection="column" gap="8px">
        <Flex alignItems="center" gap="12px">
          <Typography variant="h3">System Prompt</Typography>
          <Checkbox
            isChecked={overrideEnabled}
            onChange={(e) => setOverrideEnabled(e.target.checked)}
          >
            Override
          </Checkbox>
        </Flex>

        {overrideEnabled ? (
          <Textarea
            value={promptOverride}
            onChange={(e) => setPromptOverride(e.target.value)}
            style={{
              width: '100%',
              minHeight: '600px',
              resize: 'vertical',
              fontSize: '12px',
              fontFamily: 'monospace',
              padding: '8px',
            }}
          />
        ) : (
          <Block
            padding="16px"
            borderRadius="4px"
            backgroundColor="surface2"
            overflow="auto"
            maxHeight="600px"
          >
            <ReactMarkdown>{DEFAULT_PROMPTS[analysisMode]}</ReactMarkdown>
          </Block>
        )}
      </Flex>

      <Flex>
        <Button
          text="Run Bulk Analysis"
          onClick={handleSubmit}
          disabled={isSubmitting}
          size="sm"
        />
      </Flex>
    </Flex>
  );
};
